import React, { Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams, useHistory } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import {
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
    defaultVmDataDisk,
    isConfigDiskFulfilled,
} from 'pages/provisioning/private-cloud/sql-iaas/store';
import { checkAndRedirectCatalog } from 'utils';
import { resetOrder, resetToggleSspModal, formOrderObjFromCart, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/ShowCostDetails'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/private-cloud/sql-iaas/ConfirmDD'));

const PCSqlIaasProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [vmDataDisk, setVmDataDisk] = useRecoilState(defaultVmDataDisk);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const history = useHistory();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        setVmDataDisk({
            ...vmDataDisk,
            diskSizeInGib: data?.provisioning?.requestPayloadDefinition?.extra_vars?.azure_windows_vm_host?.os_disk_size_gb,
        });
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = formOrderObjFromCart(cartItem, data?.images?.sku);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                emptyCost: costs,
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails, data?.images?.sku);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetIsMandatoryTagsFullfilled(false);
        resetIsConfigDiskFulfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetQuickConfig(undefined);
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            // Check if user has access to this catalog, redirect if not
            const shouldRedirect = checkAndRedirectCatalog(catalogCode, history);
            if (shouldRedirect) {
                return; // Exit early if redirected
            }
            setLoading(true);
            reset();
            setVmDataDisk(undefined);
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                        <ConfirmDD />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(PCSqlIaasProvisioning);
